export const MAX_SITES = 5;

export const hostOf = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/:\d+$/, "")
    .replace(/\.$/, "");

export const rootOf = (host) => String(host || "").replace(/^www\./, "");

export const LOOKS = [
  { key: "match", label: "Match my site's fonts and colors" },
  { key: "classic", label: "Classic sans serif" },
  { key: "serif", label: "Serif editorial" },
  { key: "minimal", label: "Minimal, text only" },
];

export const LAYOUTS = [
  { key: "grid", label: "Grid, 3 across" },
  { key: "list", label: "List, one per row" },
];

export const PLATFORMS = [
  { key: "luxurypresence", label: "Luxury Presence", method: "support", supportEmail: "support@luxurypresence.com", code: "iframe" },
  { key: "agentimage", label: "Agent Image", method: "plugin", supportEmail: "support@agentimage.com", code: "script" },
  { key: "wordpress", label: "WordPress", method: "plugin" },
  { key: "kvcore", label: "kvCORE or BoldTrail", method: "line" },
  { key: "realgeeks", label: "Real Geeks", method: "line" },
  { key: "squarespace", label: "Squarespace", method: "line" },
  { key: "wix", label: "Wix", method: "line" },
  { key: "webflow", label: "Webflow", method: "line" },
  { key: "other", label: "Another builder or custom code", method: "line" },
  { key: "none", label: "I can't edit my website", method: "hosted" },
];

export const platformOf = (key) => PLATFORMS.find((item) => item.key === key) || PLATFORMS.find((item) => item.key === "other");

export const LINE_STEPS = {
  kvcore: {
    steps: [
      "In BoldTrail (kvCORE), open Website, then Pages, and open the page where the buyers should appear.",
      "Add a Custom HTML block, or switch the page editor to Source.",
      "Paste the line and select Save.",
    ],
    note: "",
  },
  realgeeks: {
    steps: [
      "In the Real Geeks Site Editor, open the page where the buyers should appear.",
      "Add an HTML block and paste the line.",
      "Select Save, then Publish.",
    ],
    note: "",
  },
  squarespace: {
    steps: [
      "Edit the page and add a Code block where the buyers should appear.",
      "Paste the line and leave Display Source off.",
      "Select Apply, then Save.",
    ],
    note: "Squarespace runs code on Core plans and above. On the Personal plan, use the hosted page instead.",
  },
  wix: {
    steps: [
      "In your Wix dashboard, open Settings, then Custom Code, and select Add Custom Code.",
      "Paste the line, choose the page where the buyers should appear, and place the code in Body, end.",
      "Select Apply. The section shows below that page's content.",
    ],
    note: "Custom code needs a Wix Premium plan with your own domain connected.",
  },
  webflow: {
    steps: [
      "Drag an Embed element onto the page where the buyers should appear.",
      "Paste the line and select Save & Close.",
      "Publish the site.",
    ],
    note: "",
  },
  other: {
    steps: [
      "Paste the line into the page HTML where the buyers should appear.",
      "Publish the page as usual.",
      "Open the page once. Your websites shows Live within a minute.",
    ],
    note: "",
  },
};

export const PLUGIN_STEPS = [
  "In WordPress, open Plugins, then Add New, then Upload Plugin, and upload the plugin file.",
  "Activate it, open Settings, then Buyer Want-Listings, and paste your key.",
  "Edit the page where the buyers should appear, add the Buyer Want-Listings block, and select Update.",
];

export const PLACEMENTS = [
  { value: "below-heading", label: "Below the page heading" },
  { value: "above-footer", label: "Above the footer" },
  { value: "whole-page", label: "As the whole page content" },
];

export const placementLabel = (value) => (PLACEMENTS.find((item) => item.value === value) || PLACEMENTS[0]).label;

export const supportEmailOf = ({ platform, brokerageName, host, pageUrl, placement, code }) => {
  const subject = `Please add our Buyer Want-Listings section to ${host}`;
  const body = [
    "Hello,",
    "",
    `Please add the Buyer Want-Listings section to our website, ${host}.`,
    "",
    `Page: ${pageUrl || `a new page named Buyers on ${host}`}`,
    `Placement: ${placementLabel(placement)}`,
    "",
    "Code to embed:",
    code || "",
    "",
    `The section fits the page width and only loads on the website addresses we listed, so please publish it on ${host}.`,
    "",
    "Thank you,",
    brokerageName || "",
  ].join("\n");
  return {
    to: platform.supportEmail,
    subject,
    body,
    mailto: `mailto:${platform.supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
  };
};

export const shortDateOf = (value) =>
  value ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

export const dateOf = (value) =>
  value ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

export const checkLineOf = (site) => {
  const check = site.check || {};
  const when = shortDateOf(check.checkedAt);
  const seen = shortDateOf(site.lastSeenAt);
  if (!site.pageUrl) return seen ? { status: "live", text: `Seen live on ${seen}`, action: "Add the page" } : { status: "pending", text: "No page saved", action: "Add the page" };
  if (check.status === "live") return { status: "live", text: `Live on ${when}`, action: null };
  if (check.status === "missing")
    return { status: "missing", text: check.alertedAt ? `Missing on ${when}, emailed you` : `Missing on ${when}`, action: "Fix it" };
  if (check.status === "unreachable") return { status: "unreachable", text: `Page did not open on ${when}`, action: "Check the page" };
  return seen ? { status: "live", text: `Seen live on ${seen}`, action: null } : { status: "pending", text: "First check within a day", action: null };
};

export const themeLineOf = (theme) => {
  if (!theme) return null;
  const font = String(theme.headingFont || theme.bodyFont || "").split(",")[0].replace(/"/g, "").trim();
  return [font, theme.accent].filter(Boolean).join(", ") || null;
};

export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand?.("copy");
    area.remove();
    return Boolean(ok);
  }
};

const DEV_HOST = /(^localhost$)|(\.localhost$)|(\.test$)|(^127\.0\.0\.1$)/i;

export const isDevHost = (host) => DEV_HOST.test(String(host || ""));

export const siteKeyOf = (host) => String(host || "").toLowerCase().replace(/^www\./, "");

export const visibleSitesOf = (sites, allowDev = false) => {
  const byKey = new Map();
  (sites || []).forEach((site) => {
    if (!allowDev && isDevHost(site.host)) return;
    const key = siteKeyOf(site.host);
    const kept = byKey.get(key);
    if (!kept || (!kept.lastSeenAt && site.lastSeenAt)) byKey.set(key, site);
  });
  return [...byKey.values()];
};
